import * as React from 'react'
import { FormBuilder } from 'components/FormBuilder'
import { useForm } from 'react-hook-form'
import { registerLoyalty } from 'services/auth'
import dayjs from 'dayjs'

import { useYupValidationResolver } from 'hooks/useYupValidationResolver'
import { formatAPIErrors } from 'util/formatApiErrors'

import { userContext } from 'global/context/sub-contexts/userContext'
import { useFingerPrint } from 'hooks/useFingerPrint'
import analytics from 'global/AnalyticsData'
import sessionStorageWrapper from 'util/SessionStorageWrapper'
import signUpFormJson from 'components/SignUpForm/signUpForm.json'
import * as styles from 'components/SignUpForm/SignUpForm.module.scss'
import { generateDynamicYupSchema } from 'components/FormBuilder/generateYupSchemaFromJson'
import { CircularProgress, Dialog, Button, IconButton } from '@mui/material'
import { UserVerification } from '../UserVerification/UserVerification'
import closeLogo from 'images/close.svg'

export const SignUpForm = ({
  formHeader = null,
  onSignUpSuccess,
  userVerificationPaperClassName = {},
  userVerificationTitleClassName = '',
}) => {
  const [verificationDialogOpen, setVerificationDialogOpen] =
    React.useState(false)
  const user_context = React.useContext(userContext)
  const [registrationError, setRegistrationError] = React.useState()
  const fingerPrintResults = useFingerPrint()

  const jsonValidationYup = generateDynamicYupSchema(signUpFormJson.fields)

  const [isInProgress, setIsInProgress] = React.useState(false)
  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      birthday: dayjs().subtract(21, 'year').toDate(),
    },
    resolver: useYupValidationResolver(jsonValidationYup),
  })
  const {
    handleSubmit,
    clearErrors,
    setValue,
    setError,
    formState,
    register,
    getValues,
  } = form

  const onEmailVerifiedConfirm = async ({ values, confirmed }) => {
    if (confirmed) {
      const {
        email,
        password,
        firstName,
        lastName,
        birthday,
        gender,
        extraField,
        captchaResponseTokenV3,
      } = getValues()
      try {
        clearErrors()
        const res = await registerLoyalty({
          email,
          password,
          firstName,
          lastName,
          captchaResponseToken: values.recaptchaResponseToken,
          captchaResponseTokenV3,
          // for date API requires a date that looks like this: "YYYY-MM-DD"
          birthDate: birthday
            ? new Date(birthday).toISOString().split('T')[0]
            : null,
          gender,
          storeIds: [],
          visitorId: fingerPrintResults?.visitorId
            ? fingerPrintResults.visitorId
            : null,
          seekEmail: '',
          profile: {
            fields: JSON.stringify(extraField),
          },
          transaction_id: sessionStorageWrapper.getItem('transaction_id'),
        })

        if (!res?.success) {
          console.warn(`BR-9657111`)
          res?.detail?.errors?.forEach(error => {
            console.log(error)
            setError(error.source, {
              type: 'server',
              message: error.detail,
            })
          })
          if (res?.detail.error) {
            setRegistrationError(formatAPIErrors(res.detail))
          }
          if (res?.errors) {
            setRegistrationError(res?.errors.detail)
          }
          setVerificationDialogOpen(false)

          return
        }

        if (res?.success) {
          user_context.setProfile(res.detail.user)
          user_context.setPoints(res.detail.user.points)

          analytics.setUser(res.detail.user)
          analytics.userEvent('registration', res.detail.user)

          if (onSignUpSuccess) {
            onSignUpSuccess()
          }
        }
      } catch (error) {
        setVerificationDialogOpen(false)
      }
    }
  }

  const onSubmit = async data => {
    setIsInProgress(true)
    await handleRegistration({
      email: data.email,
      password: data.password,
      birthDate: data.birthday,
      firstName: data.firstName,
      lastName: data.lastName,
      gender: data.gender,
      extraField: data.extraField,
      storeIds: [],
    })
    setIsInProgress(false)
  }

  const handleRegistration = async () => {
    try {
      setVerificationDialogOpen(true)
    } catch (error) {
      setRegistrationError(formatAPIErrors(error.detail))
    }
  }

  const handleClose = () => {
    setVerificationDialogOpen(false)
  }

  return (
    <>
      <FormBuilder
        form={form}
        formHeader={formHeader || <img src="/img/logo-blue.svg" alt="logo" />}
        onSubmit={handleSubmit(onSubmit)}
        register={register}
        fields={signUpFormJson.fields}
        setFieldValue={setValue}
        errors={registrationError}
        jsonFieldOnForm={'extraField'}
      >
        {formState.errors?.server && (
          <div className="error">{formState.errors.server.message}</div>
        )}
        <Button
          disabled={isInProgress || !form.formState.isValid}
          className={styles.button}
          variant="contained"
          type="submit"
        >
          {isInProgress ? (
            <CircularProgress color="white" size={18} />
          ) : (
            'Sign Up'
          )}
        </Button>
      </FormBuilder>
      <Dialog
        onClose={(_, reason) => reason !== 'backdropClick' && handleClose()}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        open={verificationDialogOpen}
        PaperProps={{
          classes: {
            root: userVerificationPaperClassName,
          },
        }}
      >
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            right: 8,
            top: 8,
            alignSelf: 'end',
          }}
        >
          <img src={closeLogo} />
        </IconButton>
        <UserVerification
          onConfirm={onEmailVerifiedConfirm}
          {...getValues()}
          titleClassName={userVerificationTitleClassName}
          paperClassName={userVerificationPaperClassName}
        />
      </Dialog>
    </>
  )
}
